import React, { useState, useEffect } from "react";
import {
  FaUser,
  FaHeadset,
  FaSearch,
  FaTimes,
  FaBars,
  FaShoppingCart,
  FaHeart,
  FaTruck,
  FaUserPlus,
} from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import "../styles/Header.css";
import AutoComplete from "./AutoComplete";
import { selectProducts } from "../selectors/ProductSelector";
import { AiOutlineUser } from "react-icons/ai";
import { fetchCategories } from "../actions/categoriesActions";
import { fetchCart } from "../actions/cartActions";
import { fetchProducts, fetchAllProducts } from "../actions/ProductActions";
import { fetchUserReferrals } from "../actions/referralActions";
import MobileSidebar from "./MobileSidebar";
import { useImage } from "../contexts/ImageContext";

const Header = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isNavVisible, setIsNavVisible] = useState(false);
  const [isMobileSearchVisible, setIsMobileSearchVisible] = useState(false);
  const [isMobileSidebarVisible, setIsMobileSidebarVisible] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const products = useSelector(selectProducts);
  const allProducts = useSelector((state) => state.products.allProducts);
  const { items: cartItems = [] } = useSelector((state) => state.cart);
  const { categories } = useSelector((state) => state.categories);
  const { userReferrals = [] } = useSelector((state) => state.referral || {});
  const [isMobile, setIsMobile] = useState(false);
  const [showReferralDropdown, setShowReferralDropdown] = useState(false);
  const [hoveredCategory, setHoveredCategory] = useState(null);

  const cartItemCount = cartItems.length;
  const { getImageUrl } = useImage();

  useEffect(() => {
    dispatch(fetchProducts(1, 30, ""));
    dispatch(fetchCategories());
    dispatch(fetchCart(getImageUrl));
    dispatch(fetchAllProducts(10000, ""));
  }, [dispatch]);

  useEffect(() => {
    if (user && user.user_id) {
      dispatch(fetchUserReferrals());
    }
  }, [dispatch, user]);

  useEffect(() => {
    setIsMobile(window.innerWidth < 768);
  }, []);

  const getSuggestions = (value) => {
    if (!value.trim()) {
      return [];
    }

    const inputValue = value.trim().toLowerCase();

    const productSuggestions = allProducts
      .filter((product) => product && product.name)
      .map((product) => ({
        text: product.name,
        type: "product",
        id: product.product_id,
      }));

    const categoryParentSuggestions = categories
      .filter((category) => category && category.name)
      .map((category) => ({
        text: category.name,
        type: "category",
        id: category.category_id,
      }));

    const categoryChildSuggestions = categories.reduce((acc, parentCat) => {
      if (parentCat.children && parentCat.children.length > 0) {
        parentCat.children.forEach((childCat) => {
          acc.push({
            text: childCat.name,
            type: "category",
            id: childCat.category_id,
            parent: parentCat.name,
          });
        });
      }
      return acc;
    }, []);

    const categoryGrandchildSuggestions = categories.reduce(
      (acc, parentCat) => {
        if (parentCat.children && parentCat.children.length > 0) {
          parentCat.children.forEach((childCat) => {
            if (childCat.children && childCat.children.length > 0) {
              childCat.children.forEach((grandchildCat) => {
                acc.push({
                  text: grandchildCat.name,
                  type: "category",
                  id: grandchildCat.category_id,
                  parent: childCat.name,
                  grandparent: parentCat.name,
                });
              });
            }
          });
        }
        return acc;
      },
      [],
    );

    const allSuggestions = [
      ...productSuggestions,
      ...categoryParentSuggestions,
      ...categoryChildSuggestions,
      ...categoryGrandchildSuggestions,
    ]
      .filter((item) => item.text.toLowerCase().includes(inputValue))
      .slice(0, 10);

    return allSuggestions;
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    const newSuggestions = getSuggestions(value);
    console.log("Setting suggestions:", newSuggestions);
    setSuggestions(newSuggestions);
  };

  const handleSuggestionClick = (suggestion) => {
    if (suggestion.type === "category") {
      navigate(`/category/${suggestion.id}`);
    } else {
      navigate(`/search?q=${suggestion.text}`);
    }

    setSearchTerm("");
    setSuggestions([]);
    setIsMobileSearchVisible(false); // Close mobile search modal
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const trimmedSearchTerm = searchTerm.trim();
    if (trimmedSearchTerm) {
      navigate(`/search?q=${trimmedSearchTerm}`);
      setSearchTerm("");
      setSuggestions([]);
      setIsMobileSearchVisible(false); // Close mobile search modal
    }
  };

  const handleCategoryClick = (categoryId) => {
    navigate(`/search?category=${categoryId}`);
  };

  const toggleNavVisibility = () => {
    setIsNavVisible(!isNavVisible);
  };

  const handleCloseAutocomplete = () => {
    setSuggestions([]);
  };

  const toggleMobileSearch = () => {
    setIsMobileSearchVisible(!isMobileSearchVisible);
    if (isMobileSearchVisible) {
      setSearchTerm("");
      setSuggestions([]);
    }
  };

  const toggleMobileSidebar = () => {
    setIsMobileSidebarVisible(!isMobileSidebarVisible);
  };

  // Recursive function to count total nested children
  const countNestedChildren = (category) => {
    if (
      !category.children ||
      !Array.isArray(category.children) ||
      category.children.length === 0
    ) {
      return 0;
    }

    let count = category.children.length;
    category.children.forEach((child) => {
      count += countNestedChildren(child);
    });

    return count;
  };

  // Sort categories by number of nested children (descending)
  const sortCategoriesByChildren = (categoriesList) => {
    if (!categoriesList || !Array.isArray(categoriesList)) {
      return [];
    }

    return [...categoriesList].sort((a, b) => {
      const aCount = countNestedChildren(a);
      const bCount = countNestedChildren(b);
      return bCount - aCount; // Descending order (more children first)
    });
  };

  const renderAllCategories = () => {
    // Filter to only show top-level categories (those without a parent)
    const topLevelCategories =
      categories?.filter((cat) => !cat.parent_id || cat.parent_id === null) ||
      [];

    // Sort parent categories by number of nested children
    const sortedTopLevelCategories =
      sortCategoriesByChildren(topLevelCategories);

    const selectedCategory =
      hoveredCategory ||
      (sortedTopLevelCategories && sortedTopLevelCategories.length > 0
        ? sortedTopLevelCategories[0]
        : null);

    if (!sortedTopLevelCategories || sortedTopLevelCategories.length === 0) {
      return (
        <div className="dropdown-content">
          <div className="dropdown-inner">
            <div className="parent-categories">
              <p>No categories available</p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="dropdown-content">
        <div className="dropdown-inner">
          <div className="parent-categories">
            {sortedTopLevelCategories.map((parentCat) => (
              <button
                key={parentCat.category_id}
                className={`parent-category ${
                  hoveredCategory?.category_id === parentCat.category_id
                    ? "active"
                    : ""
                }`}
                onClick={() => handleCategoryClick(parentCat.category_id)}
                onMouseEnter={() => {
                  setHoveredCategory(parentCat);
                }}
              >
                {parentCat.name}
                {parentCat.product_count > 0 && (
                  <span className="product-count-badge">
                    ({parentCat.product_count})
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="allcategories-right-section">
            {selectedCategory && (
              <div className="allcategories-categories-container">
                {/* Show all nested categories in a flat indexed structure */}
                {selectedCategory.children &&
                Array.isArray(selectedCategory.children) &&
                selectedCategory.children.length > 0 ? (
                  <div className="category-level-section">
                    {sortCategoriesByChildren(selectedCategory.children).map(
                      (childCat) => {
                        const hasGrandchildren =
                          childCat.children &&
                          Array.isArray(childCat.children) &&
                          childCat.children.length > 0;
                        return (
                          <div
                            key={childCat.category_id}
                            className="category-group"
                          >
                            <button
                              className="category-group-title"
                              onClick={() =>
                                handleCategoryClick(childCat.category_id)
                              }
                            >
                              {childCat.name}
                              {childCat.product_count > 0 && (
                                <span className="product-count-badge">
                                  ({childCat.product_count})
                                </span>
                              )}
                            </button>
                            {/* Show grandchildren */}
                            {hasGrandchildren && (
                              <div className="category-group-items">
                                {sortCategoriesByChildren(
                                  childCat.children,
                                ).map((grandchildCat) => {
                                  const hasGreatGrandchildren =
                                    grandchildCat.children &&
                                    Array.isArray(grandchildCat.children) &&
                                    grandchildCat.children.length > 0;
                                  return (
                                    <div
                                      key={grandchildCat.category_id}
                                      className="category-subgroup"
                                    >
                                      <button
                                        className="category-subgroup-title"
                                        onClick={() =>
                                          handleCategoryClick(
                                            grandchildCat.category_id,
                                          )
                                        }
                                      >
                                        {grandchildCat.name}
                                        {grandchildCat.product_count > 0 && (
                                          <span className="product-count-badge">
                                            ({grandchildCat.product_count})
                                          </span>
                                        )}
                                      </button>
                                      {/* Show great-grandchildren if they exist */}
                                      {hasGreatGrandchildren && (
                                        <div className="category-subgroup-items">
                                          {sortCategoriesByChildren(
                                            grandchildCat.children,
                                          ).map((greatGrandchildCat) => (
                                            <button
                                              key={
                                                greatGrandchildCat.category_id
                                              }
                                              className="category-item-link"
                                              onClick={() =>
                                                handleCategoryClick(
                                                  greatGrandchildCat.category_id,
                                                )
                                              }
                                            >
                                              {greatGrandchildCat.name}
                                              {greatGrandchildCat.product_count >
                                                0 && (
                                                <span className="product-count-badge">
                                                  (
                                                  {
                                                    greatGrandchildCat.product_count
                                                  }
                                                  )
                                                </span>
                                              )}
                                            </button>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      },
                    )}
                  </div>
                ) : (
                  <div className="no-subcategories-message">
                    <p>No subcategories available</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };
  const renderCategoryDropdown = (category) => {
    // Recursive function to render nested subcategories
    const renderNestedSubcategories = (subcategories, level = 0) => {
      if (!subcategories || subcategories.length === 0) return null;

      return subcategories.map((subcat) => {
        const hasChildren = subcat.children && subcat.children.length > 0;
        return (
          <div
            key={subcat.category_id}
            className={`nested-subcategory-level-${level}`}
          >
            <button
              className={`subcategory-item level-${level}`}
              onClick={() => handleCategoryClick(subcat.category_id)}
            >
              {subcat.name}
            </button>
            {hasChildren && (
              <div className={`nested-subcategory-children level-${level + 1}`}>
                {renderNestedSubcategories(subcat.children, level + 1)}
              </div>
            )}
          </div>
        );
      });
    };

    return (
      <div className="dropdown-content">
        <div className="dropdown-inner">
          <div className="parent-categories">
            <button
              className="parent-category"
              onClick={() => handleCategoryClick(category.category_id)}
            >
              {category.name} <span>products</span>
              {category.product_count > 0 && (
                <span className="product-count-badge">
                  ({category.product_count})
                </span>
              )}
            </button>
            <div className="child-list">
              {sortCategoriesByChildren(category.children || []).map(
                (child) => (
                  <button
                    key={child.category_id}
                    className="child-link"
                    onClick={() => handleCategoryClick(child.category_id)}
                  >
                    {child.name}
                    {child.product_count > 0 && (
                      <span className="product-count-badge">
                        ({child.product_count})
                      </span>
                    )}
                  </button>
                ),
              )}
            </div>
          </div>

          <div className="grandchild-section">
            {category.children?.map((child) => {
              const hasChildren =
                child.children &&
                Array.isArray(child.children) &&
                child.children.length > 0;
              return (
                <div key={child.category_id} className="grandchild-column">
                  <button
                    className="grandchild-title"
                    onClick={() => handleCategoryClick(child.category_id)}
                  >
                    {child.name}
                    {child.product_count > 0 && (
                      <span className="product-count-badge">
                        ({child.product_count})
                      </span>
                    )}
                  </button>
                  {hasChildren && (
                    <div className="grandchild-items">
                      {child.children.map((grandchild) => {
                        const hasGreatGrandchildren =
                          grandchild.children &&
                          Array.isArray(grandchild.children) &&
                          grandchild.children.length > 0;
                        return (
                          <div key={grandchild.category_id}>
                            <button
                              className="grandchild-item"
                              onClick={() =>
                                handleCategoryClick(grandchild.category_id)
                              }
                            >
                              {grandchild.name}
                              {grandchild.product_count > 0 && (
                                <span className="product-count-badge">
                                  ({grandchild.product_count})
                                </span>
                              )}
                            </button>
                            {/* Render deeper levels if they exist */}
                            {hasGreatGrandchildren && (
                              <div className="great-grandchild-items">
                                {sortCategoriesByChildren(
                                  grandchild.children,
                                ).map((greatGrandchild) => (
                                  <button
                                    key={greatGrandchild.category_id}
                                    className="great-grandchild-item"
                                    onClick={() =>
                                      handleCategoryClick(
                                        greatGrandchild.category_id,
                                      )
                                    }
                                  >
                                    {greatGrandchild.name}
                                    {greatGrandchild.product_count > 0 && (
                                      <span className="product-count-badge">
                                        ({greatGrandchild.product_count})
                                      </span>
                                    )}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <header>
      <div className="header-wrapper">
        <div className="container header-nav-container">
          <Link to="/" className="logo-container">
            <div className="logo">
              <span className="logo-text black">K</span>
              <span className="logo-text green">-Shop</span>
            </div>
            <span className="storefront-tagline">
              Curated products from Asia
            </span>
          </Link>

          <form className="search-bar desktop-search" onSubmit={handleSearch}>
            <div className="search-input-wrapper">
              <input
                type="text"
                className="search-input"
                placeholder="Search for products or categories..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
              <button type="submit" className="search-button">
                <FaSearch />
              </button>
              {suggestions.length > 0 && (
                <AutoComplete
                  suggestions={suggestions}
                  searchTerm={searchTerm}
                  onSuggestionClick={handleSuggestionClick}
                  onClose={handleCloseAutocomplete}
                />
              )}
            </div>
          </form>

          <div className="icons-container">
            {!isMobile ? (
              <>
                <button
                  className="icon-item mobile-search-trigger"
                  onClick={toggleMobileSearch}
                >
                  <FaSearch className="icon" />
                  <span>Search</span>
                </button>
                {user && user.username ? (
                  <Link to="/myaccount" className="icon-item">
                    <FaUser className="icon" />
                    <span>{user.username}</span>
                  </Link>
                ) : (
                  <Link to="/signin" className="icon-item">
                    <AiOutlineUser className="icon" />
                    <span>Sign in</span>
                  </Link>
                )}
                <Link to="/cart" className="icon-item">
                  <div className="cart-icon-container">
                    <FaShoppingCart className="icon" />
                    {cartItemCount > 0 && (
                      <span className="header-cart-badge">{cartItemCount}</span>
                    )}
                  </div>
                  <span>Cart</span>
                </Link>
                <Link to="/myaccount#wishlist" className="icon-item">
                  <FaHeart className="icon" />
                  <span>Wishlist</span>
                </Link>
                {user && user.user_id && (
                  <Link
                    to="/create-referral"
                    className="icon-item"
                    style={{ textDecoration: "none", color: "inherit" }}
                  >
                    <FaUserPlus className="icon" />
                    <span>Referral</span>
                  </Link>
                )}
                <Link to="/contactus" className="icon-item">
                  <FaHeadset className="icon" />
                  <span>Help</span>
                </Link>
                <Link to="/track-order" className="icon-item">
                  <FaTruck className="icon" />
                  <span>Track Order</span>
                </Link>
              </>
            ) : (
              <div div className="nav-buttons flex">
                <button
                  className="nav-button-item"
                  onClick={toggleMobileSearch}
                >
                  <FaSearch className="icon" />
                  <span>Search</span>
                </button>

                {user && user.username ? (
                  <Link to="/myaccount" className="nav-button-item">
                    <FaUser className="icon" />
                    <span>{user.username.slice(0, 10)}</span>
                  </Link>
                ) : (
                  <Link to="/signin" className="nav-button-item">
                    <AiOutlineUser className="icon" />
                    <span>Sign in</span>
                  </Link>
                )}
              </div>
            )}
          </div>

          <button className="menu-icon" onClick={toggleMobileSidebar}>
            <FaBars />
          </button>
        </div>
      </div>
      {isMobileSearchVisible && (
        <div className="mobile-search-overlay">
          <form onSubmit={handleSearch}>
            <div className="search-input-wrapper">
              <input
                type="text"
                className="search-input"
                placeholder="Search for products or categories..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
              <button
                type="button"
                className="close-search"
                onClick={toggleMobileSearch}
              >
                <FaTimes />
              </button>
              {suggestions.length > 0 && (
                <AutoComplete
                  suggestions={suggestions}
                  searchTerm={searchTerm}
                  onSuggestionClick={handleSuggestionClick}
                  onClose={handleCloseAutocomplete}
                />
              )}
            </div>
          </form>
        </div>
      )}
      <nav className={`desktop-nav ${isNavVisible ? "visible" : ""}`}>
        <button className="close-menu" onClick={toggleNavVisibility}>
          <FaTimes />
        </button>
        <div className="nav-container">
          <div className="nav-items">
            <Link to="/all-products">
              <button>All Products</button>
            </Link>
            <div className="nav-item">
              <button className="nav-button">Categories</button>
              {renderAllCategories()}
            </div>
            <Link to="/best">
              <button>Best</button>
            </Link>
            <Link to="/new">
              <button>New</button>
            </Link>
            <Link to="/deals">
              <button>Sale</button>
            </Link>
            {/* <Link to="/routines">
              <button>Routines</button>
            </Link> */}
          </div>
          <div className="nav-items">
            <button onClick={() => navigate("/brands")}>Brands</button>
          </div>
        </div>
      </nav>
      <MobileSidebar
        isVisible={isMobileSidebarVisible}
        onClose={() => setIsMobileSidebarVisible(false)}
        categories={categories}
        handleCategoryClick={handleCategoryClick}
      />
    </header>
  );
};
export default Header;
